// Enumerations recovered from the original program, with the original display labels.

export type PartyIndex = 0 | 1;
export const otherParty = (p: PartyIndex): PartyIndex => (p === 0 ? 1 : 0);

export type FilingStatus = 'MFJIN' | 'MFJOUT' | 'HHMLA' | 'SINGLE' | 'MFSIN' | 'MFSOUT';
export const FILING_STATUSES: FilingStatus[] = ['MFJIN', 'MFJOUT', 'HHMLA', 'SINGLE', 'MFSIN', 'MFSOUT'];
/** IRS status used by the tax engines. MFJIN/MFJOUT -> MFJ, MFSIN/MFSOUT -> MFS. */
export type IrsStatus = 'MFJ' | 'HOH' | 'SINGLE' | 'MFS';
export const toIrsStatus = (s: FilingStatus): IrsStatus =>
  s === 'MFJIN' || s === 'MFJOUT' ? 'MFJ' : s === 'HHMLA' ? 'HOH' : s === 'SINGLE' ? 'SINGLE' : 'MFS';
/** On-screen label. The arrow points toward the other on-screen party for the "-in" statuses. */
export function filingStatusLabel(s: FilingStatus, party: PartyIndex): string {
  switch (s) {
    case 'MFJIN': return party === 0 ? 'MFJ->' : '<-MFJ';
    case 'MFJOUT': return party === 0 ? '<-MFJ' : 'MFJ->';
    case 'MFSIN': return party === 0 ? 'MFS->' : '<-MFS';
    case 'MFSOUT': return party === 0 ? '<-MFS' : 'MFS->';
    case 'HHMLA': return 'HH/MLA';
    case 'SINGLE': return 'SINGLE';
  }
}

export type CsMethod = 'USER' | 'GUIDELINE';
export const CS_METHOD_LABEL: Record<CsMethod, string> = { USER: 'User CS', GUIDELINE: 'Guideln CS' };

export type SsMethod = 'USER' | 'FORMULA' | 'SANTA_CLARA' | 'ALAMEDA' | 'MARIN' | 'KINGS' | 'SAN_DIEGO' | 'HUMBOLDT' | 'YOLO';
export const SS_METHODS: SsMethod[] = ['USER', 'FORMULA', 'SANTA_CLARA', 'ALAMEDA', 'MARIN', 'KINGS', 'SAN_DIEGO', 'HUMBOLDT', 'YOLO'];
export const SS_METHOD_LABEL: Record<SsMethod, string> = {
  USER: 'User SS', FORMULA: 'Formula SS', SANTA_CLARA: 'S.Clara SS', ALAMEDA: 'Alameda SS', MARIN: 'Marin SS',
  KINGS: 'Kings SS', SAN_DIEGO: 'S.Diego SS', HUMBOLDT: 'Humboldt SS', YOLO: 'Yolo SS',
};

export type CceAllocation = 'FIFTY_FIFTY' | 'FC4061B' | 'AFTER_SUPPORT' | 'BEFORE_SUPPORT' | 'CP_DEDUCTION';
export const CCE_ALLOCATION_LABEL: Record<CceAllocation, string> = {
  FIFTY_FIFTY: '50/50', FC4061B: 'FC 4061(b)', AFTER_SUPPORT: 'After support', BEFORE_SUPPORT: 'Before support', CP_DEDUCTION: 'CP Deduction',
};

/** How the CS payor's net is adjusted for CS when computing spousal support (and the user formula's ADJUST option). */
export type NetAdjustMethod = 'NONE' | 'ACTUAL_CS' | 'PERCENT_ALLOCATED' | 'DOLLAR_ALLOCATED';
export const NET_ADJUST_LABEL: Record<NetAdjustMethod, string> = {
  NONE: 'Do not adjust', ACTUAL_CS: 'Actual CS', PERCENT_ALLOCATED: '% allocated', DOLLAR_ALLOCATED: '$$ allocated',
};
export const NET_ADJUST_LONG_LABEL: Record<NetAdjustMethod, string> = {
  NONE: "Do not make any adjustment to CS payor's net",
  ACTUAL_CS: "Subtract actual CS paid from CS payor's net",
  PERCENT_ALLOCATED: 'Subtract % of net allocated for CS (S.Clara/Alameda method)',
  DOLLAR_ALLOCATED: 'Subtract $ amount of net allocated for CS (S.Diego method)',
};

export type HardshipLimitBase = 'PRESUMED_CS' | 'BASIC_CS' | 'CS_ALLOCATION';
export const HARDSHIP_LIMIT_LABEL: Record<HardshipLimitBase, string> = { PRESUMED_CS: 'Presumed CS', BASIC_CS: 'Basic CS', CS_ALLOCATION: 'CS Allocation' };

export type TimeshareUnit = 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
/** Size of one unit in months (recovered: hours 1/730, days 12/365, weeks 12/52.18, months 1, years 12). */
export const UNIT_MONTHS: Record<TimeshareUnit, number> = { HOUR: 1 / 730, DAY: 12 / 365, WEEK: 12 / 52.18, MONTH: 1, YEAR: 12 };
export const UNIT_LABEL: Record<TimeshareUnit, string> = { HOUR: 'Hours', DAY: 'Days', WEEK: 'Weeks', MONTH: 'Months', YEAR: 'Years' };

export type TimeshareMode = 'HOURS' | 'SANTA_CLARA';

export type Mode = 'MONTHLY' | 'ANNUAL';

export type TaxYear = 2022 | 2023 | 2024 | 2025 | 2026 | 2027;
export const TAX_YEARS: TaxYear[] = [2022, 2023, 2024, 2025, 2026, 2027];

export type Tactic = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'a' | 'b' | 'c' | 'd' | 'e' | 'f';
export const TACTIC_DESCRIPTION: Record<Tactic, string> = {
  '0': 'Make Guideline total fully deductible',
  '1': 'Fix specific dollar support (non-deductible/deductible)',
  '2': 'Minimize taxes, then specify net spendable income for HUSBAND',
  '3': 'Minimize taxes, then specify net spendable income for WIFE',
  '4': 'Minimize taxes, then specify % of combined net spendable to HUSBAND',
  '5': 'Minimize taxes, then specify % of combined net spendable to WIFE',
  '6': 'Minimize taxes, then specify % of increase over GDL to go to HUSBAND',
  '7': 'Minimize taxes, then specify % of increase over GDL to go to WIFE',
  '8': 'Minimize taxes, then split combined net spendable in Guideline ratio',
  '9': 'Release all dependency exemptions, then use Guideline award',
  a: 'Find best combined result (but no two-way payment)',
  b: 'Find best combined result (two-way payment OK)',
  c: 'Set a limit on deductible support',
  d: 'Fix specific deductible support',
  e: 'Fix specific NON-deductible support',
  f: 'Find best family (all deductible) support',
};
/** Sub-options for tactics 2..8 (recovered from the engine). */
export type TacticSub = 0 | 1 | 2 | 3 | 4 | 5;
export const TACTIC_SUB_LABEL: Record<TacticSub, string> = {
  0: 'Find best combined result (but no two-way payment)',
  1: 'Find best combined result (two-way payment OK)',
  2: 'Set a limit on deductible support',
  3: 'Fix specific deductible support',
  4: 'Fix specific NON-deductible support',
  5: 'Find best family (all deductible) support',
};

export type MfsDeductionMethod = 'ITEMIZE' | 'STANDARD';

/** Party naming sets; chosen automatically from case facts. */
export type PartyLabelSet = 'HUSBAND_WIFE' | 'FATHER_MOTHER' | 'PARTY_AB' | 'PARTNER_AB';
export const PARTY_LABELS: Record<PartyLabelSet, [string, string]> = {
  HUSBAND_WIFE: ['Husband', 'Wife'], FATHER_MOTHER: ['Father', 'Mother'], PARTY_AB: ['Party A', 'Party B'], PARTNER_AB: ['Partner A', 'Partner B'],
};

/** Santa Clara County timeshare table (letter, description, days per year). Percent = days / 365. */
export const SANTA_CLARA_TABLE: { key: string; description: string; days: number }[] = [
  { key: 'a', description: '1 weekend/mo', days: 24 },
  { key: 'b', description: '1 extended weekend/mo', days: 36 },
  { key: 'c', description: '2 weekends/mo', days: 48 },
  { key: 'd', description: '1 weekend/mo + 1 evening/wk', days: 50 },
  { key: 'e', description: 'Alternate weekends', days: 52 },
  { key: 'f', description: 'Alternate weekends + 2 wks summer', days: 67 },
  { key: 'g', description: 'Alternate weekends & 1/2 holidays + 2 wks summer', days: 69 },
  { key: 'h', description: '2 extended weekends/mo', days: 72 },
  { key: 'i', description: 'Alternate weekends + 1 evening/wk', days: 78 },
  { key: 'j', description: 'Alternate weekends + 1 overnight/wk', days: 104 },
  { key: 'k', description: 'Alternate extended weekends', days: 78 },
  { key: 'l', description: 'Alternate weekends & 1/2 holidays + 4 wks summer (alt weekends summer)', days: 77 },
  { key: 'm', description: 'Alternate weekends & 1/2 holidays + 4 wks summer (no alt weekends summer)', days: 75 },
  { key: 'n', description: 'Alternate weekends & 1/2 holidays + 1/2 summer', days: 82 },
  { key: 'o', description: 'Alternate extended weekends +1 evening/wk', days: 104 },
  { key: 'p', description: 'Alternate extended weekends +1 overnight/wk', days: 130 },
  { key: 'q', description: 'Alternate weekends & 1/2 holidays, 1 evening/wk + 4 wks summer (alt weekends summer)', days: 103 },
  { key: 'r', description: 'Alternate weekends and 1 evening/wk during school, +1/2 school vacations', days: 104 },
  { key: 's', description: 'Three days/wk', days: 156 },
];
